/**
 * Hedita Database Types
 * Generated from Supabase migrations
 * All IDs are UUID strings for type safety
 */

export type UUID = string;

// ============================================================
// TABELLA: strutture
// ============================================================
export interface Struttura {
  id: UUID;
  creato_il: string;
  aggiornato_il: string;
  utente_id: UUID;
  amministratore_id: UUID;
  nome: string;
  tipo: 'casa_vacanze' | 'b&b' | 'hotel' | 'affittacamere' | string;
  stato: 'attiva' | 'inattiva' | 'in_manutenzione' | string;
  indirizzo: string | null;
  citta: string | null;
  regione: string | null;
  cap: string | null;
  telefono: string | null;
  email: string | null;
  descrizione: string | null;
  num_camere: number;
  max_ospiti: number;
  metri_quadri: number | null;
  // Extended fields from migration 004
  slogan: string | null;
  paese: string;
  visibilita: string;
  piano: string | null;
  ospiti_max: number;
  superficie: number;
  parcheggio: string;
  link_prenotazione: string | null;
  cin: string | null;
  pet_friendly: boolean;
  host_assegnato: string | null;
  foto_principale: string | null;
  logo: string | null;
  servizi: string;
  istruzioni_checkin: string | null;
  istruzioni_checkout: string | null;
  regole_casa: string | null;
  dotazioni: string | null;
  info_private_extra: string;
  cose_da_fare: string | null;
  dove_mangiare: string | null;
  trasporti_zona: string | null;
  parcheggio_note: string | null;
  da_sapere_extra: string;
  descrizione_posizione: string | null;
  latitudine: string | null;
  longitudine: string | null;
  mappa_embed: string | null;
  // Campi aggiuntivi usati nell'app ma mancanti nel tipo
  host_id: UUID | null;
  provincia: string | null;
  sito_web: string | null;
  posti_letto_totali: number;
  check_in_time: string;
  check_out_time: string;
  data_apertura: string | null;
  note: string | null;
}

export type StrutturaInsert = Omit<Struttura, 'id' | 'creato_il' | 'aggiornato_il'>;
export type StrutturaUpdate = Partial<Omit<Struttura, 'id' | 'creato_il'>>;

// ============================================================
// TABELLA: camere
// ============================================================
export interface Camera {
  id: UUID;
  creato_il: string;
  aggiornato_il: string;
  utente_id: UUID;
  amministratore_id: UUID;
  struttura_id: UUID;
  codice: string | null;
  tipo: string | null;
  posti: number;
  piano: string | null;
  stato: 'Disponibile' | 'Occupata' | 'In pulizia' | 'Non disponibile' | string;
  note: string | null;
}

export type CameraInsert = Omit<Camera, 'id' | 'creato_il' | 'aggiornato_il'>;
export type CameraUpdate = Partial<Omit<Camera, 'id' | 'creato_il'>>;

// ============================================================
// TABELLA: prenotazioni
// ============================================================
export interface Prenotazione {
  id: UUID;
  creato_il: string;
  aggiornato_il: string;
  utente_id: UUID;
  amministratore_id: UUID;
  struttura_id: UUID;
  camera_id: UUID | null;
  ospite_id: UUID | null;
  ospite_nome: string | null;
  ospite_email: string | null;
  ospite_telefono: string | null;
  data_checkin: string;
  data_checkout: string;
  stato: 'confermata' | 'in_attesa' | 'cancellata' | 'completata' | string;
  num_ospiti: number;
  importo_totale: number | null;
  canale: 'diretto' | 'airbnb' | 'booking' | 'expedia' | 'altro' | string;
  note: string | null;
}

export type PrenotazioneInsert = Omit<Prenotazione, 'id' | 'creato_il' | 'aggiornato_il'>;
export type PrenotazioneUpdate = Partial<Omit<Prenotazione, 'id' | 'creato_il'>>;

// ============================================================
// TABELLA: ospiti
// ============================================================
export interface Ospite {
  id: UUID;
  creato_il: string;
  aggiornato_il: string;
  utente_id: UUID;
  amministratore_id: UUID;
  struttura_id: UUID | null;
  camera_id: UUID | null;
  nome: string;
  cognome: string | null;
  email: string | null;
  telefono: string | null;
  note: string | null;
}

export type OspiteInsert = Omit<Ospite, 'id' | 'creato_il' | 'aggiornato_il'>;
export type OspiteUpdate = Partial<Omit<Ospite, 'id' | 'creato_il'>>;

// ============================================================
// TABELLA: transazioni (finanze)
// ============================================================
export interface Transazione {
  id: UUID;
  creato_il: string;
  aggiornato_il: string;
  utente_id: UUID;
  amministratore_id: UUID;
  struttura_id: UUID | null;
  camera_id: UUID | null;
  tipo: 'Entrata' | 'Uscita';
  descrizione: string | null;
  categoria: string | null;
  importo: number | null;
  data: string | null;
  struttura_nome: string | null;
}

export type TransazioneInsert = Omit<Transazione, 'id' | 'creato_il' | 'aggiornato_il'>;
export type TransazioneUpdate = Partial<Omit<Transazione, 'id' | 'creato_il'>>;

// ============================================================
// TABELLA: collaboratori
// ============================================================
export interface Collaboratore {
  id: UUID;
  creato_il: string;
  aggiornato_il: string;
  utente_id: UUID;
  amministratore_id: UUID;
  struttura_id: UUID | null;
  nome: string;
  cognome: string | null;
  email: string | null;
  telefono: string | null;
  ruolo: string | null;
  attivo: boolean;
  note: string | null;
}

export type CollaboratoreInsert = Omit<Collaboratore, 'id' | 'creato_il' | 'aggiornato_il'>;
export type CollaboratoreUpdate = Partial<Omit<Collaboratore, 'id' | 'creato_il'>>;

// ============================================================
// TABELLA: host_profiles
// ============================================================
export interface HostProfile {
  id: UUID;
  creato_il: string;
  aggiornato_il: string;
  utente_id: UUID;
  amministratore_id: UUID;
  nome_display: string;
  ruolo: string;
  bio: string | null;
  avatar: string | null;
  verificato: boolean;
  paese: string | null;
  lingue: string[];
  interessi: string[];
  // Extended fields from migration 008
  email: string | null;
  regione: string | null;
  telefono: string | null;
  email_contatto: string | null;
  whatsapp: string | null;
  foto_copertina: string | null;
  profile_boost: boolean;
  punteggio: number;
  num_recensioni: number;
  tempo_risposta: number;
  strutture_ids: string;
  strutture_nomi: string;
}

export type HostProfileInsert = Omit<HostProfile, 'id' | 'creato_il' | 'aggiornato_il'>;
export type HostProfileUpdate = Partial<Omit<HostProfile, 'id' | 'creato_il'>>;

// ============================================================
// TABELLA: attivita
// ============================================================
export interface Attivita {
  id: UUID;
  creato_il: string;
  aggiornato_il: string;
  utente_id: UUID;
  amministratore_id: UUID;
  struttura_id: UUID | null;
  camera_id: UUID | null;
  prenotazione_id: UUID | null;
  collaboratore_id: UUID | null;
  titolo: string;
  descrizione: string | null;
  tipo: string | null;
  stato: 'da_fare' | 'in_corso' | 'completata' | 'annullata' | string;
  priorita: 'bassa' | 'normale' | 'alta' | 'urgente' | string;
  data_scadenza: string | null;
  assegnato_a: UUID | null;
}

export type AttivitaInsert = Omit<Attivita, 'id' | 'creato_il' | 'aggiornato_il'>;
export type AttivitaUpdate = Partial<Omit<Attivita, 'id' | 'creato_il'>>;

// ============================================================
// TABELLA: attivita_pulizie
// ============================================================
export interface AttivitaPulizia {
  id: UUID;
  created_at: string;
  updated_at: string;
  user_id: UUID;
  struttura_id: UUID | null;
  camera_id: UUID | null;
  data: string;
  ora_inizio: string | null;
  ora_fine: string | null;
  stato: 'da_fare' | 'in_corso' | 'completata' | string;
  note: string | null;
  assegnato_a: UUID | null;
}

export type AttivitaPuliziaInsert = Omit<AttivitaPulizia, 'id' | 'created_at' | 'updated_at'>;
export type AttivitaPuliziaUpdate = Partial<Omit<AttivitaPulizia, 'id' | 'created_at'>>;

// ============================================================
// TABELLA: manutenzione
// ============================================================
export interface ManutenzioneTicket {
  id: UUID;
  created_at: string;
  updated_at: string;
  user_id: UUID;
  struttura_id: UUID | null;
  titolo: string;
  descrizione: string | null;
  priorita: 'bassa' | 'normale' | 'alta' | 'urgente' | string;
  stato: 'aperto' | 'in_lavorazione' | 'risolto' | 'chiuso' | string;
  costo: number | null;
  data_segnalazione: string;
  data_risoluzione: string | null;
  assegnato_a: UUID | null;
}

export type ManutenzioneTicketInsert = Omit<ManutenzioneTicket, 'id' | 'created_at' | 'updated_at'>;
export type ManutenzioneTicketUpdate = Partial<Omit<ManutenzioneTicket, 'id' | 'created_at'>>;

// ============================================================
// TABELLA: segnalazioni
// ============================================================
export interface Segnalazione {
  id: UUID;
  created_at: string;
  updated_at: string;
  user_id: UUID;
  struttura_id: UUID | null;
  titolo: string;
  descrizione: string | null;
  tipo: string | null;
  priorita: 'bassa' | 'normale' | 'alta' | 'urgente' | string;
  stato: 'aperta' | 'in_esame' | 'in_corso' | 'risolta' | 'chiusa' | string;
  segnalato_da: string | null;
}

export type SegnalazioneInsert = Omit<Segnalazione, 'id' | 'created_at' | 'updated_at'>;
export type SegnalazioneUpdate = Partial<Omit<Segnalazione, 'id' | 'created_at'>>;

// ============================================================
// TABELLA: recensioni
// ============================================================
export interface Recensione {
  id: UUID;
  created_at: string;
  updated_at: string;
  user_id: UUID;
  struttura_id: UUID | null;
  prenotazione_id: UUID | null;
  ospite_nome: string | null;
  voto: number | null;
  testo: string | null;
  piattaforma: string | null;
  data: string;
  risposta: string | null;
}

export type RecensioneInsert = Omit<Recensione, 'id' | 'created_at' | 'updated_at'>;
export type RecensioneUpdate = Partial<Omit<Recensione, 'id' | 'created_at'>>;

// ============================================================
// TABELLA: ical_sync
// ============================================================
export interface ICalSync {
  id: UUID;
  created_at: string;
  updated_at: string;
  user_id: UUID;
  struttura_id: UUID;
  nome: string;
  url: string;
  tipo: 'import' | 'export' | string;
  attivo: boolean;
  ultimo_sync: string | null;
}

export type ICalSyncInsert = Omit<ICalSync, 'id' | 'created_at' | 'updated_at'>;
export type ICalSyncUpdate = Partial<Omit<ICalSync, 'id' | 'created_at'>>;

// ============================================================
// TABELLA: guest_app_config
// ============================================================
export interface GuestAppConfig {
  id: UUID;
  created_at: string;
  updated_at: string;
  user_id: UUID;
  struttura_id: UUID;
  titolo: string | null;
  messaggio_benvenuto: string | null;
  wifi_nome: string | null;
  wifi_password: string | null;
  check_in_ora: string | null;
  check_out_ora: string | null;
  regole: string | null;
  contatto_emergenza: string | null;
  attivo: boolean;
  colore_primario: string;
  logo_url: string | null;
}

export type GuestAppConfigInsert = Omit<GuestAppConfig, 'id' | 'created_at' | 'updated_at'>;
export type GuestAppConfigUpdate = Partial<Omit<GuestAppConfig, 'id' | 'created_at'>>;

// ============================================================
// TABELLA: avvisi
// ============================================================
export interface Avviso {
  id: UUID;
  created_at: string;
  updated_at: string;
  user_id: UUID;
  struttura_id: UUID | null;
  titolo: string;
  messaggio: string | null;
  tipo: 'Info' | 'Warning' | 'Error' | 'Success' | string;
  attivo: boolean;
}

export type AvvisoInsert = Omit<Avviso, 'id' | 'created_at' | 'updated_at'>;
export type AvvisoUpdate = Partial<Omit<Avviso, 'id' | 'created_at'>>;

// ============================================================
// TABELLA: contratti
// ============================================================
export interface Contratto {
  id: UUID;
  creato_il: string;
  aggiornato_il: string;
  utente_id: UUID;
  amministratore_id: UUID;
  prenotazione_id: UUID | null;
  struttura_id: UUID | null;
  ospite_nome: string | null;
  ospite_email: string | null;
  testo: string | null;
  stato: 'bozza' | 'inviato' | 'firmato' | 'annullato' | string;
  firmato_il: string | null;
  url_firmato: string | null;
}

export type ContrattoInsert = Omit<Contratto, 'id' | 'created_at' | 'updated_at'>;
export type ContrattoUpdate = Partial<Omit<Contratto, 'id' | 'created_at'>>;

// ============================================================
// TABELLA: contratto_templates
// ============================================================
export interface ContrattoTemplate {
  id: UUID;
  created_at: string;
  updated_at: string;
  user_id: UUID;
  nome: string;
  tipo: string;
  contenuto: string | null;
  attivo: boolean;
}

export type ContrattoTemplateInsert = Omit<ContrattoTemplate, 'id' | 'created_at' | 'updated_at'>;
export type ContrattoTemplateUpdate = Partial<Omit<ContrattoTemplate, 'id' | 'created_at'>>;

// ============================================================
// TABELLA: notifiche
// ============================================================
export interface Notifica {
  id: UUID;
  created_at: string;
  updated_at: string;
  user_id: UUID;
  tipo: 'prenotazione' | 'check-in' | 'recensione' | 'pagamento' | 'manutenzione' | 'segnalazione' | 'contratto' | 'messaggio' | string;
  titolo: string;
  messaggio: string | null;
  letta: boolean;
  link: string | null;
}

export type NotificaInsert = Omit<Notifica, 'id' | 'created_at' | 'updated_at'>;
export type NotificaUpdate = Partial<Omit<Notifica, 'id' | 'created_at'>>;

// ============================================================
// TABELLA: schema_migrations (metadata)
// ============================================================
export interface SchemaMigration {
  version: string;
  executed_at: string;
  description: string | null;
}

// ============================================================
// Database Tables Enum
// ============================================================
export enum DatabaseTable {
  PROPERTIES = 'strutture',
  CAMERE = 'camere',
  PRENOTAZIONI = 'prenotazioni',
  OSPITI = 'ospiti',
  TRANSAZIONI = 'transazioni',
  COLLABORATORI = 'collaboratori',
  HOST_PROFILES = 'host_profiles',
  ATTIVITA = 'attivita',
  ATTIVITA_PULIZIE = 'attivita_pulizie',
  MANUTENZIONE = 'manutenzione',
  SEGNALAZIONI = 'segnalazioni',
  RECENSIONI = 'recensioni',
  ICAL_SYNC = 'ical_sync',
  GUEST_APP_CONFIG = 'guest_app_config',
  AVVISI = 'avvisi',
  CONTRATTI = 'contratti',
  CONTRACT_TEMPLATES = 'contract_templates',
  NOTIFICHE = 'notifiche',
  SCHEMA_MIGRATIONS = 'schema_migrations',
}

// ============================================================
// Utility Types for API Responses
// ============================================================
export type DatabaseResponse<T> = {
  data: T | null;
  error: Error | null;
};

export type DatabaseListResponse<T> = {
  data: T[] | null;
  error: Error | null;
  count: number | null;
};

// ============================================================
// Query Filters
// ============================================================
export interface QueryFilters {
  eq?: Record<string, unknown>;
  neq?: Record<string, unknown>;
  gt?: Record<string, unknown>;
  gte?: Record<string, unknown>;
  lt?: Record<string, unknown>;
  lte?: Record<string, unknown>;
  like?: Record<string, string>;
  ilike?: Record<string, string>;
  is?: Record<string, null | boolean>;
  in?: Record<string, unknown[]>;
  contains?: Record<string, unknown[]>;
  containedBy?: Record<string, unknown[]>;
  range?: Record<string, [number, number]>;
  overlaps?: Record<string, unknown[]>;
  textSearch?: Record<string, { query: string; options?: string }>;
  search?: string;
  not?: QueryFilters;
  or?: QueryFilters[];
  and?: QueryFilters[];
}

export interface QueryOptions {
  filters?: QueryFilters;
  order?: { column: string; ascending?: boolean };
  limit?: number;
  offset?: number;
  single?: boolean;
  maybeSingle?: boolean;
  csv?: boolean;
  count?: 'exact' | 'planned' | 'estimated';
}
